/**
 * @file ExportPreview.js
 * @version 1.2.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-09
 * @modified 2026-10-02
 * @description The document, as it will print, beside the choices that shape it.
 *
 *              The server returns the SAME html string it hands to Puppeteer —
 *              same template, same stylesheet, same fit script — so this is the
 *              document rather than a drawing of it. A preview built in React
 *              would be a second implementation of the layout and would drift
 *              from the PDF the moment either changed, which is the one thing a
 *              preview must not do.
 *
 *              Rendered in a sandboxed iframe. The document carries a script
 *              (the fit modes measure themselves), so `allow-scripts` is
 *              required — but NOT `allow-same-origin`, which would let that
 *              script reach this page's DOM and cookies. Without the pair it
 *              runs in an opaque origin and can only measure and scale itself.
 *
 *              Debounced, because it re-renders on every option change and the
 *              payload can carry a few hundred rows. 350ms is under the point
 *              where a control feels disconnected from its result.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { API_URL } from '../config';
import './ExportPreview.css';

// A4 at 96dpi. The iframe is laid out at true page size and then scaled to the
// pane, so what is shown is a page rather than a narrow reflow of one.
const PAGE_W_PORTRAIT = 794;
const PAGE_W_LANDSCAPE = 1123;
const ASPECT = 1122.5 / 793.7;

const DEBOUNCE_MS = 350;

// mm → CSS px at 96dpi.
const MM = 96 / 25.4;

// What the PDF is printed with, if the server does not say. Kept in step with
// DOCUMENT_MARGINS_MM in services/puppeteerPdfService.js, which is the source.
const DEFAULT_MARGINS = { top: 12, right: 14, bottom: 16, left: 14 };

/**
 * @param multiPage  A document that runs to many pages. The pane scrolls and
 *                   the iframe grows to its content instead of being clipped to
 *                   one sheet — a 53-customer report showed only its cover
 *                   otherwise. The document reports its own height by
 *                   postMessage, because the sandbox deliberately withholds
 *                   allow-same-origin and its DOM cannot be read from here.
 */
function ExportPreview({ endpoint, payload, disabled = false, note = '', multiPage = false }) {
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [landscape, setLandscape] = useState(false);
  const [margins, setMargins] = useState(DEFAULT_MARGINS);
  const [scale, setScale] = useState(1);
  const [contentH, setContentH] = useState(0);

  const paneRef = useRef(null);
  // The payload is an object rebuilt on every render; comparing its JSON is what
  // stops an identical rebuild from firing another request.
  const signature = JSON.stringify(payload || null);

  // The document posts its height once it has laid itself out.
  useEffect(() => {
    if (!multiPage) return undefined;
    const onMessage = (e) => {
      if (e.data?.type === 'preview-height' && Number.isFinite(e.data.height)) {
        setContentH(Math.ceil(e.data.height));
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [multiPage]);

  const fetchPreview = useCallback(async (body, signal) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
        signal
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not build the preview');
      setHtml(data.html || '');
      setLandscape(!!data.landscape);
      if (data.margins) setMargins(data.margins);
    } catch (e) {
      if (e.name === 'AbortError') return;
      setError(e.message);
      setHtml('');
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  useEffect(() => {
    if (disabled || !payload) { setHtml(''); return undefined; }
    const controller = new AbortController();
    const timer = setTimeout(() => fetchPreview(payload, controller.signal), DEBOUNCE_MS);
    return () => { clearTimeout(timer); controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, disabled, fetchPreview]);

  /* Fit the page to the pane, whatever the pane happens to be. */
  useEffect(() => {
    const el = paneRef.current;
    if (!el) return undefined;
    const measure = () => {
      const pageW = landscape ? PAGE_W_LANDSCAPE : PAGE_W_PORTRAIT;
      const pageH = Math.round(pageW * (landscape ? 1 / ASPECT : ASPECT));
      // clientWidth includes the pane's own padding. Measuring without taking
      // it off made the page a little too big for the room, so it overflowed
      // and sat hard against one edge instead of in an even border.
      const cs = window.getComputedStyle(el);
      const padX = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
      const padY = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
      const availW = el.clientWidth - padX;
      const availH = el.clientHeight - padY;
      // Whichever dimension runs out first. Fitting to width alone left a
      // portrait page floating in the top of a much taller pane, with a third
      // of the panel empty — and on a short window it overflowed instead.
      const fit = multiPage
        ? (availW > 0 ? availW / pageW : 1)
        : Math.min(
            availW > 0 ? availW / pageW : 1,
            availH > 0 ? availH / pageH : 1
          );
      setScale(Number.isFinite(fit) && fit > 0 ? Math.min(1, fit) : 1);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [landscape, multiPage]);

  const pageW = landscape ? PAGE_W_LANDSCAPE : PAGE_W_PORTRAIT;
  const pageH = Math.round(pageW * (landscape ? 1 / ASPECT : ASPECT));
  // The document is laid out in the printable area, not across the whole
  // sheet. Previously the iframe was the full page width, so the preview showed
  // content running to the paper's edge and wrapping at a wider measure than
  // the PDF — which is inset by these margins — ever does.
  const inset = {
    top: margins.top * MM,
    right: margins.right * MM,
    bottom: margins.bottom * MM,
    left: margins.left * MM,
  };
  const printableW = Math.round(pageW - inset.left - inset.right);
  const printableH = Math.round(pageH - inset.top - inset.bottom);

  return (
    <div className="export-preview">
      <div className="export-preview-head">
        <span className="export-preview-title">Preview</span>
        {loading && <span className="export-preview-status">updating…</span>}
        {!loading && note && <span className="export-preview-status">{note}</span>}
      </div>

      <div className="export-preview-pane" ref={paneRef}>
        {error && <div className="export-preview-error">{error}</div>}
        {!error && !html && !loading && (
          <div className="export-preview-empty">Nothing to preview yet.</div>
        )}
        {!error && html && (
          <div
            className="export-preview-page"
            style={{
              width: pageW * scale,
              height: (multiPage && contentH ? contentH + inset.top + inset.bottom : pageH) * scale,
              paddingTop: inset.top * scale,
              paddingRight: inset.right * scale,
              paddingBottom: inset.bottom * scale,
              paddingLeft: inset.left * scale,
            }}
          >
            <div
              className="export-preview-printable"
              style={{
                width: printableW * scale,
                height: (multiPage && contentH ? contentH : printableH) * scale
              }}
            >
              <iframe
                title="Export preview"
                /* No allow-same-origin: the document runs a script and must not
                   be able to reach this page. */
                sandbox="allow-scripts"
                srcDoc={html}
                style={{
                  width: printableW,
                  height: multiPage && contentH ? contentH : printableH,
                  transform: `scale(${scale})`,
                  transformOrigin: 'top left'
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ExportPreview;
